import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BadgeCheck, Building2, Clock3, Pencil } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { API } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { useJazyk } from '../../context/LanguageContext';

/**
 * Převzetí katalogového profilu organizací, která k němu patří.
 *
 * Údaje o organizacích stárnou rychleji, než je stihne někdo z Bloomu hlídat.
 * Když se o profil stará samotná organizace, přestane katalog záviset na tom,
 * jestli si někdo změny všimne.
 *
 * Převzetí ale dává právo měnit, co se o organizaci píše, takže se nikdy neděje
 * samo — žádost posoudí moderace. Proto se ptáme na dvě věci: čím v organizaci
 * dotyčný člověk je a jak se to dá ověřit. Bez druhého se rozhodnout nedá.
 */

// Rozcestníky jsou vlastní obsah Bloomu — ty nepatří nikomu zvenčí.
const LZE_PREVZIT = ['organizations', 'events', 'pageants'];

// Názvy druhů jsou ve slovníku (`prevzeti.druh.*`) – text „Patří vám tahle
// organizace?" se skloňuje s nimi.

export default function ClaimProfile({ kind, zaznam, onZmena }) {
  const { user } = useAuth();
  const { t } = useJazyk();
  const [zadosti, setZadosti] = useState(null);
  const [otevrenaZadost, setOtevrenaZadost] = useState(false);
  const [otevrenaUprava, setOtevrenaUprava] = useState(false);

  const lzePrevzit = LZE_PREVZIT.includes(kind);
  const jsemSpravce = !!user && (zaznam.claimed_by === user.id
    || (zaznam.admin_ids || []).includes(user.id));
  const uzSpravovano = !!zaznam.claimed_by;

  useEffect(() => {
    // Vlastní žádosti načítáme jen tehdy, když je co řešit — u profilu, který
    // už někdo spravuje, by odpověď stejně nic nezměnila.
    if (!lzePrevzit || !user || jsemSpravce || uzSpravovano) { setZadosti([]); return; }
    axios.get(`${API}/my/directory-claims`)
      .then((r) => setZadosti(r.data))
      .catch(() => setZadosti([]));
  }, [lzePrevzit, user, jsemSpravce, uzSpravovano, zaznam.id]);

  if (!lzePrevzit || !user) return null;

  const cekaSe = (zadosti || []).some(
    (z) => z.entry_id === zaznam.id && z.status === 'pending');

  if (jsemSpravce) {
    return (
      <>
        <div className="mt-6 rounded-2xl border border-bloom-mint/40 bg-bloom-mint/10 p-4 flex flex-wrap items-center gap-3">
          <BadgeCheck className="w-5 h-5 text-bloom-mint shrink-0" />
          <p className="text-sm text-bloom-text flex-1 min-w-[12rem]">
            {t('prevzeti.spravujes')}
          </p>
          <Button variant="outline" onClick={() => setOtevrenaUprava(true)}
                  data-testid="upravit-profil">
            <Pencil className="w-3.5 h-3.5 mr-1.5" />{t('prevzeti.upravit')}
          </Button>
        </div>
        <UpravaDialog open={otevrenaUprava} onOpenChange={setOtevrenaUprava}
                      kind={kind} zaznam={zaznam} onZmena={onZmena} />
      </>
    );
  }

  if (uzSpravovano) {
    return (
      <p className="mt-6 text-xs text-bloom-sub flex items-center gap-1.5">
        <Building2 className="w-3.5 h-3.5" />
        {t('prevzeti.spravujeSama', { druh: t(`prevzeti.druh.${kind}`) })}
      </p>
    );
  }

  if (cekaSe) {
    return (
      <p className="mt-6 text-xs text-bloom-sub flex items-center gap-1.5"
         data-testid="zadost-ceka">
        <Clock3 className="w-3.5 h-3.5" />
        {t('prevzeti.ceka')}
      </p>
    );
  }

  return (
    <>
      <div className="mt-6 rounded-2xl border border-border/50 bg-bloom-bg p-4">
        <p className="text-sm text-bloom-text">
          {t('prevzeti.patriVam', { druh: t(`prevzeti.druh.${kind}`) })}
        </p>
        <p className="mt-1 text-xs text-bloom-sub leading-relaxed">
          {t('prevzeti.popis')}
        </p>
        <Button variant="outline" className="mt-3" onClick={() => setOtevrenaZadost(true)}
                data-testid="prevzit-profil">
          {t('prevzeti.tlacitko')}
        </Button>
      </div>
      <ZadostDialog open={otevrenaZadost} onOpenChange={setOtevrenaZadost}
                    kind={kind} zaznam={zaznam}
                    onOdeslano={() => setZadosti([...(zadosti || []),
                      { entry_id: zaznam.id, status: 'pending' }])} />
    </>
  );
}


function ZadostDialog({ open, onOpenChange, kind, zaznam, onOdeslano }) {
  const { t } = useJazyk();
  const [role, setRole] = useState('');
  const [doklad, setDoklad] = useState('');
  const [kontakt, setKontakt] = useState('');
  const [zprava, setZprava] = useState('');
  const [odesila, setOdesila] = useState(false);

  useEffect(() => {
    if (open) { setRole(''); setDoklad(''); setKontakt(''); setZprava(''); }
  }, [open]);

  const odesli = async () => {
    if (role.trim().length < 3 || doklad.trim().length < 3) {
      toast.error(t('prevzeti.vyplnObe'));
      return;
    }
    setOdesila(true);
    try {
      await axios.post(`${API}/directory/${kind}/${zaznam.id}/claim`,
        { role, proof: doklad, contact: kontakt, message: zprava });
      toast.success(t('prevzeti.prijato'));
      onOdeslano();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('prevzeti.chyba'));
    } finally {
      setOdesila(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('prevzeti.formulacTitulek', { nazev: zaznam.name })}</DialogTitle>
          <DialogDescription>{t('prevzeti.formulacPopis')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-sm">{t('prevzeti.role')}</Label>
            <Input value={role} onChange={(e) => setRole(e.target.value)}
                   placeholder={t('prevzeti.rolePriklad')}
                   className="mt-1" data-testid="claim-role" />
          </div>
          <div>
            <Label className="text-sm">{t('prevzeti.doklad')}</Label>
            <Textarea value={doklad} onChange={(e) => setDoklad(e.target.value)}
                      rows={3} className="mt-1" data-testid="claim-doklad"
                      placeholder={t('prevzeti.dokladPriklad')} />
            <p className="mt-1 text-xs text-bloom-sub">
              {t('prevzeti.dokladNapoveda')}
            </p>
          </div>
          <div>
            <Label className="text-sm">
              {t('prevzeti.kontakt')}{' '}
              <span className="text-bloom-sub font-normal">{t('prevzeti.nepovinne')}</span>
            </Label>
            <Input value={kontakt} onChange={(e) => setKontakt(e.target.value)}
                   placeholder={t('prevzeti.kontaktPriklad')} className="mt-1" />
          </div>
          <div>
            <Label className="text-sm">
              {t('prevzeti.dalsi')}{' '}
              <span className="text-bloom-sub font-normal">{t('prevzeti.nepovinne')}</span>
            </Label>
            <Textarea value={zprava} onChange={(e) => setZprava(e.target.value)}
                      rows={2} className="mt-1" />
          </div>
        </div>

        <Button onClick={odesli} disabled={odesila}
                className="bg-bloom-violet hover:bg-bloom-violet/90"
                data-testid="odeslat-zadost">
          {odesila ? t('prevzeti.odesilam') : t('prevzeti.odeslat')}
        </Button>
      </DialogContent>
    </Dialog>
  );
}


function UpravaDialog({ open, onOpenChange, kind, zaznam, onZmena }) {
  const { t } = useJazyk();
  const [popis, setPopis] = useState('');
  const [email, setEmail] = useState('');
  const [telefon, setTelefon] = useState('');
  const [web, setWeb] = useState('');
  const [instagram, setInstagram] = useState('');
  const [facebook, setFacebook] = useState('');
  const [uklada, setUklada] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPopis(zaznam.description || '');
    setEmail(zaznam.email || '');
    setTelefon(zaznam.phone || '');
    setWeb(zaznam.social?.website || '');
    setInstagram(zaznam.social?.instagram || '');
    setFacebook(zaznam.social?.facebook || '');
  }, [open, zaznam]);

  const uloz = async () => {
    setUklada(true);
    try {
      const { data } = await axios.put(`${API}/directory/${kind}/${zaznam.id}/managed`, {
        description: popis,
        email,
        phone: telefon,
        social: { ...(zaznam.social || {}), website: web, instagram, facebook },
      });
      toast.success(t('prevzeti.ulozeno'));
      onZmena?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('prevzeti.chybaUlozeni'));
    } finally {
      setUklada(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('prevzeti.upravaTitulek', { nazev: zaznam.name })}</DialogTitle>
          <DialogDescription>{t('prevzeti.upravaPopis')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-sm">{t('prevzeti.popisPole')}</Label>
            <Textarea value={popis} onChange={(e) => setPopis(e.target.value)}
                      rows={4} className="mt-1" data-testid="uprava-popis" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-sm">{t('prevzeti.email')}</Label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label className="text-sm">{t('prevzeti.telefon')}</Label>
              <Input value={telefon} onChange={(e) => setTelefon(e.target.value)} className="mt-1" />
            </div>
          </div>
          <div>
            <Label className="text-sm">{t('prevzeti.web')}</Label>
            <Input value={web} onChange={(e) => setWeb(e.target.value)} className="mt-1" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-sm">Instagram</Label>
              <Input value={instagram} onChange={(e) => setInstagram(e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label className="text-sm">Facebook</Label>
              <Input value={facebook} onChange={(e) => setFacebook(e.target.value)} className="mt-1" />
            </div>
          </div>
        </div>

        <Button onClick={uloz} disabled={uklada}
                className="bg-bloom-violet hover:bg-bloom-violet/90"
                data-testid="ulozit-profil">
          {uklada ? t('prevzeti.ukladam') : t('obecne.ulozit')}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
